import { createSelector, Selector } from '@reduxjs/toolkit'
import {
  Application,
  EntityType,
  FatFingerUserLimitDetail,
  Group,
  Permission,
  TradingControlLimitUserUtilisation,
  TradingLimitType,
  User,
  UserDetail,
  UserStatus
} from 'api'
import { GlobalState } from 'store'
import { createAllEntitiesSelector, createAllEntityUpdatesSelector, createEntityGetterSelector } from './entitySelectors'
import { isOwnOrganisationGetterSelector } from './adminSelectors'
import { authorisedUserSelector } from './connectionSelectors'
import { compareInsensitive, getDefaultFatFingerSector, ParametricSelector } from 'utils'
import { DecisionMakerHelper, UserHelper } from 'model'
import { showOnlyEnabledUsersSelector } from './globalGridSelectors'
import { isBuySideTraderSelector } from './permissionSelectors'

export const userDetailsSelector: Selector<GlobalState, UserDetail[]> = createSelector(
  createAllEntityUpdatesSelector(EntityType.USER),
  createEntityGetterSelector(EntityType.ORGANISATION),
  isOwnOrganisationGetterSelector,
  showOnlyEnabledUsersSelector,
  (userUpdates, getOrganisation, getIsOwnOrganisation, showOnlyEnabledUsers) => {
    return userUpdates
      .filter(update => (showOnlyEnabledUsers ? update.entity.status === UserStatus.ACTIVE : update))
      .filter(update => getIsOwnOrganisation(update.entity.organisationId))
      .map(update => {
        return {
          user: update.entity,
          organisation: getOrganisation(update.entity.organisationId),
          availableEvents: update.availableEvents
        }
      })
  }
)

export const createUsersListByOrganisationIdGetterSelector: Selector<GlobalState, (id: string | null) => string[]> = createSelector(
  createAllEntityUpdatesSelector(EntityType.USER),
  users => {
    return (id: string | null) => users.filter(u => u.entity.organisationId === id).map(user => user.entity.id)
  }
)

export const decisionMakerUserIdsSelector: Selector<GlobalState, string[]> = createSelector(
  createAllEntitiesSelector(EntityType.USER),
  isOwnOrganisationGetterSelector,
  (allUsers, getIsOwnOrganisation) =>
    allUsers
      .filter(user => getIsOwnOrganisation(user.organisationId) && user.status === UserStatus.ACTIVE && user.canTrade && user.shortId)
      .sort((user1, user2) => compareInsensitive(DecisionMakerHelper.getName(user1), DecisionMakerHelper.getName(user2)))
      .map(user => user.id)
)

export const createOrganisationDecisionMakerUserIdsSelector = <TProps>(
  organisationIdSelector: ParametricSelector<{}, TProps, string | null>
): ParametricSelector<GlobalState, TProps, string[]> =>
  createSelector([createAllEntitiesSelector(EntityType.USER), organisationIdSelector], (allUsers, organisationId) =>
    allUsers
      .filter(user => user.organisationId === organisationId && user.status === UserStatus.ACTIVE && user.shortId)
      .sort((user1, user2) => compareInsensitive(DecisionMakerHelper.getName(user1), DecisionMakerHelper.getName(user2)))
      .map(user => user.id)
  )

export const createUserByUsernameGetterSelector: Selector<
  GlobalState,
  (username: string) => User | null
> = createSelector(createAllEntitiesSelector(EntityType.USER), users => (username: string) =>
  users.find(user => user.username === username && user.status === UserStatus.ACTIVE) || null
)

export const createUserByUserIdGetterSelector: Selector<
  GlobalState,
  (userId: string) => User | null
> = createSelector(createAllEntitiesSelector(EntityType.USER), users => (userId: string) => users.find(user => user.id === userId) || null)

export const createUserNameByUserIdGetterSelector: Selector<
  GlobalState,
  (userId: string | null) => string | null
> = createSelector(createAllEntitiesSelector(EntityType.USER), users => (userId: string) =>
  users.find(user => user.id === userId)?.username || null
)

export const isAdminGatewayUserSelector: Selector<GlobalState, boolean> = createSelector(
  authorisedUserSelector,
  authorisedUser => authorisedUser.application === Application.ADMIN_GATEWAY
)

export const isTradingGatewayUserSelector: Selector<GlobalState, boolean> = createSelector(
  authorisedUserSelector,
  authorisedUser => authorisedUser.application === Application.TRADING_GATEWAY
)

export const tradingLimitTypeUserSelector: Selector<GlobalState, TradingLimitType> = createSelector(
  authorisedUserSelector,
  createEntityGetterSelector(EntityType.USER),
  (authorisedUser, getUser) => {
    const currentUser = getUser(authorisedUser.id)
    return currentUser?.tradingControlLimit.tradingLimitType || TradingLimitType.NONE
  }
)

export function createGetTradingLimitCurrenciesUserSelector<TProps>(
  userIdParametricSelector: ParametricSelector<{}, TProps, string | null | undefined>
): Selector<GlobalState, TradingControlLimitUserUtilisation | null> {
  return createSelector(
    [userIdParametricSelector, createEntityGetterSelector(EntityType.TRADING_CONTROL_LIMIT_USER_UTILISATION)],
    (userId, tradingControlLimitGetter) => tradingControlLimitGetter(userId || '')
  )
}

export const isBuySideTradeAndHasLimitTypeSelector: Selector<GlobalState, boolean> = createSelector(
  isBuySideTraderSelector,
  tradingLimitTypeUserSelector,
  (isBuySideTrader, tradingLimitTypeUser) => isBuySideTrader && tradingLimitTypeUser !== TradingLimitType.NONE
)

export const fatFingerLimitUserGetterSelector: Selector<GlobalState, (userId: string) => FatFingerUserLimitDetail[]> = createSelector(
  createAllEntitiesSelector(EntityType.FAT_FINGER_USER),
  createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  createEntityGetterSelector(EntityType.PRODUCT),
  createAllEntitiesSelector(EntityType.FAT_FINGER_PRODUCT_SECTOR),
  (fatFingerUserLimits, getProductSector, getProduct, fatFingers) => (userId: string) =>
    fatFingerUserLimits
      .filter(fatFingerLimit => fatFingerLimit.userId === userId)
      .map(element => {
        const productSector = getProductSector(element.productSectorId)
        const product = productSector && getProduct(productSector.productId)
        return {
          fatFingerUserLimit: element,
          productSector,
          product,
          defaultFatFinger: getDefaultFatFingerSector(fatFingers, element.productSectorId)
        }
      })
      .sort((tem1, tem2) => {
        return compareInsensitive(tem1.productSector?.name || '', tem2.productSector?.name || '')
      })
)

export const allUsersOrganisationUserIdsSelector: Selector<GlobalState, string[]> = createSelector(
  createAllEntitiesSelector(EntityType.USER),
  isOwnOrganisationGetterSelector,
  (allUsers, getIsOwnOrganisation) =>
    allUsers
      .filter(user => getIsOwnOrganisation(user.organisationId) && user.status === UserStatus.ACTIVE)
      .sort((user1, user2) => compareInsensitive(UserHelper.getName(user1), DecisionMakerHelper.getName(user2)))
      .map(user => user.id)
)

export const createUserHasPermissionSelector = (permission: Permission): Selector<GlobalState, boolean> =>
  createSelector(authorisedUserSelector, authorisedUser => authorisedUser.permissions.includes(permission))

export const createUserHasGroupSelector = (group: Group): Selector<GlobalState, boolean> => {
  return createSelector(authorisedUserSelector, authorisedUser => authorisedUser.groups.includes(group))
}
